import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBackOutlined,
  CheckCircleOutline,
  EastOutlined,
  RadioButtonUncheckedOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ResetPasswordService } from "../../../shared/services/api/reset-password/ResetPasswordService";
import * as yup from "yup";
import imageSite from "../../../assets/fundo_login.png";

const passwordPolicyMessage = "Use no minimo 8 caracteres, 1 letra maiuscula e 1 caractere especial.";

const resetPasswordSchema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup
    .string()
    .required("Informe a nova senha.")
    .min(8, "A senha deve ter no minimo 8 caracteres.")
    .matches(/[A-Z]/, "A senha deve conter pelo menos 1 letra maiuscula.")
    .matches(/[!@#$%^&*(),.?":{}|<>_\-\\/\[\];'`~+=]/, "A senha deve conter pelo menos 1 caractere especial."),
  confirmPassword: yup
    .string()
    .required("Confirme a nova senha.")
    .oneOf([yup.ref("password")], "Senhas devem ser iguais!"),
});

const ResetPasswordPresentation: React.FC = () => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState("");

  const [resetCallSuccessful, setResetCallSuccessful] = useState(false);

  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>_\-\\/\[\];'`~+=]/.test(password);
  const passwordRules = [
    { label: "Pelo menos 8 caracteres", valid: hasMinLength },
    { label: "Uma letra maiuscula", valid: hasUppercase },
    { label: "Um caractere especial", valid: hasSpecialCharacter },
  ];

  const handleSubmit = async () => {
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setFormError("");
    setIsLoading(true);

    try {
      await resetPasswordSchema.validate(
        { email, password, confirmPassword },
        { abortEarly: false }
      );

      try {
        await ResetPasswordService.recoverPassword({
          token,
          password,
          confirmPassword,
        });

        setResetCallSuccessful(true);
        setIsLoading(false);
      } catch (e: any) {
        setIsLoading(false);
        setFormError(e.message || "Nao foi possivel redefinir sua senha. Tente novamente.");
      }
    } catch (errors: any) {
      setIsLoading(false);

      errors.inner.forEach((error: yup.ValidationError) => {
        
        if (error.path === "email") {
          setEmailError(error.message);
        }

        if (error.path === "password") {
          setPasswordError(error.message);
        }

        if (error.path === "confirmPassword") {
          setConfirmPasswordError(error.message);
        }
      });
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const providedToken = searchParams.get("token");


      if (!providedToken) {
        return navigate("/login", { replace: true });
      }

      setToken(providedToken);

      try {
        const data = await ResetPasswordService.getUserDataByToken({
          token: providedToken,
        });

        const email = data.email;
        setEmail(email);
        setIsLoading(false);
      } catch (e: any) {
        
        alert(e.message);
        await navigate("/login");
      }
    })();
  }, []);

  const handleNavigateBack = async () => {
    setIsLoading(true);

    navigate("/login");
  };

  return (
      <Grid container height={"100vh"}>
        <Box
          zIndex={1}
          position={"absolute"}
          width={"100vw"}
          height={"100vh"}
          sx={{
            backgroundImage: `url(${imageSite})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{ width: "100%", height: "100%" }}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            {!resetCallSuccessful ? (
                <Card
                  sx={{
                    borderRadius: "16px",
                    width: "100%",
                    maxWidth: "560px",
                    minHeight: "620px",
                    height: "auto",
                    marginLeft: smDown ? 2 : "20%",
                    marginRight: 2,
                    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.16)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <CardContent sx={{ px: 4, py: 4, maxWidth: "460px" }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      width="100%"
                    >
                      <Box
                        width="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={1}
                      >
                        <img src="logo_garantti_login.png" alt="Logo" height={"80px"} />
                      </Box>

                      <Typography variant="h5" align="center" color="GrayText" fontWeight={700}>
                        Redefina sua senha
                      </Typography>
                      <Typography variant="body2" align="center" color="text.secondary">
                        Crie uma nova senha com mais seguranca para concluir o acesso a sua conta.
                      </Typography>

                      <Paper
                        elevation={0}
                        sx={{
                          mt: 1,
                          p: 2,
                          borderRadius: 3,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e7edf5",
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
                          Regras da nova senha
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                          {passwordPolicyMessage}
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1}>
                          {passwordRules.map((rule) => (
                            <Box key={rule.label} display="flex" alignItems="center" gap={1}>
                              {rule.valid ? (
                                <CheckCircleOutline sx={{ fontSize: 18, color: "#1f8a70" }} />
                              ) : (
                                <RadioButtonUncheckedOutlined sx={{ fontSize: 18, color: "#94a3b8" }} />
                              )}
                              <Typography
                                variant="body2"
                                color={rule.valid ? "#14532d" : "text.secondary"}
                                fontWeight={rule.valid ? 600 : 400}
                              >
                                {rule.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>

                      <TextField
                        fullWidth
                        type="email"
                        label="E-mail"
                        value={email}
                        disabled={true}
                        error={!!emailError}
                        helperText={emailError}
                        onKeyDown={() => setEmailError("")}
                        onChange={(e) => setEmail(e.target.value)}
                        size={"small"}
                        sx={{ mt: 1 }}
                      />

                      <TextField
                        fullWidth
                        type={showPassword ? "text" : "password"}
                        label="Nova senha"
                        value={password}
                        disabled={isLoading}
                        error={!!passwordError}
                        helperText={passwordError || ""}
                        onKeyDown={() => setPasswordError("")}
                        onChange={(e) => setPassword(e.target.value)}
                        size={"small"}
                        sx={{ mt: 1 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                onClick={() => setShowPassword((currentValue) => !currentValue)}
                                disabled={isLoading}
                                aria-label={showPassword ? "Ocultar senha" : "Exibir senha"}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      <TextField
                        fullWidth
                        type={showConfirmPassword ? "text" : "password"}
                        label="Confirme sua senha"
                        value={confirmPassword}
                        disabled={isLoading}
                        error={!!confirmPasswordError}
                        helperText={confirmPasswordError}
                        onKeyDown={() => setConfirmPasswordError("")}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        size={"small"}
                        sx={{ mt: 1 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                onClick={() => setShowConfirmPassword((currentValue) => !currentValue)}
                                disabled={isLoading}
                                aria-label={showConfirmPassword ? "Ocultar confirmacao de senha" : "Exibir confirmacao de senha"}
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      {!!formError && (
                        <Alert
                          severity="error"
                          sx={{
                            mt: 1,
                            borderRadius: 3,
                            backgroundColor: "#fef2f2",
                            color: "#991b1b",
                            "& .MuiAlert-icon": {
                              color: "#dc2626",
                            },
                          }}
                        >
                          {formError}
                        </Alert>
                      )}

                      <Alert
                        severity="info"
                        sx={{
                          mt: 1,
                          borderRadius: 3,
                          alignItems: "center",
                          backgroundColor: "#eff6ff",
                          color: "#1e3a8a",
                          "& .MuiAlert-icon": {
                            color: "#2563eb",
                          },
                        }}
                      >
                        Depois da confirmacao, voce sera redirecionado para entrar com a nova senha.
                      </Alert>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 4, pb: 4, pt: 0 }}>
                    <Box
                      display="flex"
                      flexDirection={smDown ? "column-reverse" : "row"}
                      gap={1.5}
                      width="100%"
                    >
                      <Button
                        variant="outlined"
                        disabled={isLoading}
                        onClick={handleNavigateBack}
                        startIcon={<ArrowBackOutlined />}
                        endIcon={
                          isLoading ? (
                            <CircularProgress variant="indeterminate" />
                          ) : undefined
                        }
                        sx={{
                          height: 44,
                          flex: 1,
                          borderColor: "#cbd5e1",
                          color: "#334155",
                        }}
                      >
                        Voltar
                      </Button>

                      <Button
                        variant="contained"
                        disabled={isLoading}
                        onClick={handleSubmit}
                        startIcon={!isLoading ? <EastOutlined /> : undefined}
                        endIcon={
                          isLoading ? (
                            <CircularProgress variant="indeterminate" />
                          ) : undefined
                        }
                        sx={{
                          height: 44,
                          flex: 1.2,
                          background: "#7924c7",
                          boxShadow: "0 14px 24px rgba(121, 36, 199, 0.24)",
                          "&:hover": {
                            background: "#6620aa",
                          },
                        }}
                      >
                        Confirmar
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
            ) : (
                <Card
                  sx={{
                    borderRadius: "16px",
                    width: "100%",
                    maxWidth: "520px",
                    marginLeft: smDown ? 2 : "20%",
                    marginRight: 2,
                    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.16)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent
                    sx={{ px: 4, py: 5 }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      width="100%"
                      alignItems="center"
                    >
                      <Box
                        width={72}
                        height={72}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          backgroundColor: "#ecfdf3",
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        <CheckCircleOutline sx={{ fontSize: 38, color: "#16a34a" }} />
                      </Box>

                      <Typography variant="h5" align="center" fontWeight={700}>
                        Senha alterada com sucesso!
                      </Typography>
                      <Typography variant="body2" align="center" color="text.secondary">
                        Sua senha foi atualizada. Agora voce ja pode entrar novamente com a nova credencial.
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 4, pb: 4, pt: 0 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      width="100%"
                    >
                      <Button
                        variant="contained"
                        disabled={isLoading}
                        onClick={handleNavigateBack}
                        startIcon={!isLoading ? <ArrowBackOutlined /> : undefined}
                        endIcon={
                          isLoading ? (
                            <CircularProgress variant="indeterminate" />
                          ) : undefined
                        }
                        sx={{
                          height: 44,
                          background: "#7924c7",
                          boxShadow: "0 14px 24px rgba(121, 36, 199, 0.24)",
                          "&:hover": {
                            background: "#6620aa",
                          },
                        }}
                      >
                        Ir para o login
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
            )}
          </Box>
        </Box>
      </Grid>
  );
};

export default ResetPasswordPresentation;
